import { useTranslation } from 'react-i18next';
import { useLanguageForm } from '../hooks/useLanguageForm';
import { faTrash, faEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default function LanguageForm({ person }) {
    const { t } = useTranslation();
    const {
        languageFormRegister,
        languageErrors,
        handleDelete,
        submitLanguageForm,
        handleSubmit,
        languageData,
        languageOptions,
        skillGrades,
        handleEdit,
        isEditing
    } = useLanguageForm(person);

    const onLanguageSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit(submitLanguageForm)(e);
    };

    return (
        <div className="language-form-container">
            <h3 className="form-subtitle">
                {t('persons.language.title', {
                    name: [person.name, person.fathersName, person.surname]
                        .filter(Boolean)
                        .join(' ')
                })}
            </h3>

            <div className="form-content">
                <div className="form-section">
                    <form onSubmit={onLanguageSubmit}>
                        <div className="form-input-wrapper">
                            <span className="span-bold">{t('persons.language.language')} <span className="required">*</span></span>
                            <div className='form-input-fields'>
                                <select
                                    className={`form-control ${languageErrors.language ? 'is-invalid' : ''}`}
                                    {...languageFormRegister('language')}
                                    disabled={isEditing}
                                >
                                    <option value="">{t('common.select')}</option>
                                    {languageOptions.map(option => (
                                        <option key={option.language} value={option.language}>
                                            {option.name}
                                        </option>
                                    ))}
                                </select>
                                {languageErrors.language && (
                                    <div className="invalid-feedback">{languageErrors.language.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-input-wrapper flex-row">
                            <div className='form-input-fields width-50'>
                                <span className="span-bold">{t('persons.language.listening')}</span>
                                <select
                                    className={`form-control ${languageErrors.listening ? 'is-invalid' : ''}`}
                                    {...languageFormRegister('listening')}
                                >
                                    <option value="">{t('common.select')}</option>
                                    {skillGrades.map(grade => (
                                        <option key={grade.standartLevel} value={grade.standartLevel}>
                                            {`${grade.standartLevel} - ${grade.name}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className='form-input-fields width-50'>
                                <span className="span-bold">{t('persons.language.reading')}</span>
                                <select
                                    className={`form-control ${languageErrors.reading ? 'is-invalid' : ''}`}
                                    {...languageFormRegister('reading')}
                                >
                                    <option value="">{t('common.select')}</option>
                                    {skillGrades.map(grade => (
                                        <option key={grade.standartLevel} value={grade.standartLevel}>
                                            {`${grade.standartLevel} - ${grade.name}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-input-wrapper flex-row">
                            <div className='form-input-fields width-50'>
                                <span className="span-bold">{t('persons.language.writing')}</span>
                                <select
                                    className={`form-control ${languageErrors.writing ? 'is-invalid' : ''}`}
                                    {...languageFormRegister('writing')}
                                >
                                    <option value="">{t('common.select')}</option>
                                    {skillGrades.map(grade => (
                                        <option key={grade.standartLevel} value={grade.standartLevel}>
                                            {`${grade.standartLevel} - ${grade.name}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className='form-input-fields'>
                                <span className="span-bold">{t('persons.language.speaking')}</span>
                                <select
                                    className={`form-control ${languageErrors.spokenInteract ? 'is-invalid' : ''}`}
                                    {...languageFormRegister('spokenInteract')}
                                >
                                    <option value="">{t('common.select')}</option>
                                    {skillGrades.map(grade => (
                                        <option key={grade.standartLevel} value={grade.standartLevel}>
                                            {`${grade.standartLevel} - ${grade.name}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-input-wrapper">
                            <span className="span-bold">{t('persons.language.overall')}</span>
                            <div className='form-input-fields'>
                                <select
                                    className={`form-control ${languageErrors.spokenProduction ? 'is-invalid' : ''}`}
                                    {...languageFormRegister('spokenProduction')}
                                >
                                    <option value="">{t('common.select')}</option>
                                    {skillGrades.map(grade => (
                                        <option key={grade.standartLevel} value={grade.standartLevel}>
                                            {`${grade.standartLevel} - ${grade.name}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="language-form-controls">
                            <button
                                type="button"
                                className={`form-btn ${isEditing ? 'btn-orange' : 'btn-green'}`}
                                onClick={handleSubmit(submitLanguageForm)}
                            >
                                {isEditing ? t('common.edit') : t('common.add')}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="table-section">
                    <div className="language-list-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>{t('persons.language.language')}</th>
                                    <th>{t('persons.language.listening')}</th>
                                    <th>{t('persons.language.reading')}</th>
                                    <th>{t('persons.language.writing')}</th>
                                    <th>{t('persons.language.speaking')}</th>
                                    <th>{t('persons.language.overall')}</th>
                                    <th>{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {languageData?.map((lang, index) => (
                                    <tr key={index}>
                                        <td>{languageOptions.find(opt => opt.language === lang.id.language)?.name || lang.id.language}</td>
                                        <td>{lang.listening}</td>
                                        <td>{lang.reading}</td>
                                        <td>{lang.writing}</td>
                                        <td>{lang.spokenInteract}</td>
                                        <td>{lang.spokenProduction}</td>
                                        <td className="action-buttons">
                                            <FontAwesomeIcon
                                                icon={faEdit}
                                                className="action-icon edit"
                                                onClick={() => handleEdit(lang)}
                                            />
                                            <FontAwesomeIcon
                                                icon={faTrash}
                                                className="action-icon delete"
                                                onClick={() => handleDelete({
                                                    personId: person.id,
                                                    language: lang.id.language
                                                })}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
} 